import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Observable, catchError, map, share, shareReplay, tap, throwError } from "rxjs";
import { ItemOption } from "../../../../../../iea/protocols/membership/src/lib/iea-membership/youth-rider-membership/interface/youth-rider.interface";
import { IEnvironment } from "apps/iea-app/src/environments/environment.interface";
import { toSignal } from "@angular/core/rxjs-interop";
import { ShowStatusKey } from "../interface/show-status.interface";
import { ShowStatus } from "@equine-ui-nx/ui";
import { ISeasonAggregateResponse } from "../interface/seaons-interface";

interface AdminNote {
    assetId: string;
    assetType: string;
    authorId: string;
    authorName: string;
    authorProfilePic: string;
    content: string;
    createdAt: string;
    images: string[];
    videos: string[];
    documents: string[];
    seasonId: string;
    _id: string;
}

@Injectable({
    providedIn: 'root',
})
export class CommonService {
  constructor(
      private http: HttpClient,
      @Inject('environment') private environment: IEnvironment
  ) { }

  private schoolOptions$ = this.http.get<ItemOption[]>(`${this.environment.apiUrl}/membership/schoolOptions`).pipe(
    shareReplay(1),
    catchError(this.handleError)
  );
  schoolOptionsSignal = toSignal<ItemOption[], ItemOption[]>(this.schoolOptions$, {initialValue: []}); 

  private heightOptions$ = this.http.get<ItemOption[]>(`${this.environment.apiUrl}/membership/heightOptions`).pipe(
    shareReplay(1),
    catchError(this.handleError)
  );
  heightOptionsSignal = toSignal<ItemOption[], ItemOption[]>(this.heightOptions$, {initialValue: []});

  private weightOptions$ = this.http.get<ItemOption[]>(`${this.environment.apiUrl}/membership/weightOptions`).pipe(
    shareReplay(1),
    catchError(this.handleError)
  );
  weightOptionsSignal = toSignal<ItemOption[], ItemOption[]>(this.weightOptions$, {initialValue: []});

  private organizationOptions$ = this.http.get<ItemOption[]>(`${this.environment.apiUrl}/membership/organizationOptions`).pipe(
    shareReplay(1),
    catchError(this.handleError)
  );
  organizationOptionsSignal = toSignal<ItemOption[], ItemOption[]>(this.organizationOptions$, {initialValue: []});

  private raceNEthnicityOptions$ = this.http.get<ItemOption[]>(`${this.environment.apiUrl}/membership/raceEthnicityOptions`).pipe(
    shareReplay(1),
    catchError(this.handleError)
  );
  raceNEthnicityOptionsSignal = toSignal<ItemOption[], ItemOption[]>(this.raceNEthnicityOptions$, {initialValue: []});

  genderOptions$ = this.http.get<ItemOption[]>(`${this.environment.apiUrl}/membership/genderOptions`).pipe(
    shareReplay(1),
    catchError(this.handleError)
  );
  genderOptionsSignal = toSignal<ItemOption[], ItemOption[]>(this.genderOptions$, {initialValue: []});

  disablityOptions$ = this.http.get<ItemOption[]>(`${this.environment.apiUrl}/membership/disabilityOptions`).pipe(
    shareReplay(1),
    catchError(this.handleError)
  )
  disablityOptionsSignal = toSignal<ItemOption[], ItemOption[]>(this.disablityOptions$, {initialValue: []});

  courseOptions$ = this.http.get<ItemOption[]>(`${this.environment.apiUrl}/membership/courseOptions`).pipe(
    shareReplay(1),
    catchError(this.handleError)
  );
  courseOptionsSignal = toSignal<ItemOption[], ItemOption[]>(this.courseOptions$, {initialValue: []});

  seasonsOptions$: Observable<ItemOption[]> = this.http.get<ISeasonAggregateResponse>(`${this.environment.apiUrl}/seasons/list?limit=100&aggregate=true&sort=byName`).pipe(
    shareReplay(1),
    map((data: ISeasonAggregateResponse) => { return data.data.map(season => ({ label: season.season.name, value: season._id })) }),
    catchError(this.handleError),
  );
  seasonsOptionsSignal = toSignal<ItemOption[], ItemOption[]>(this.seasonsOptions$, {initialValue: []});

  // getDesciplineOptions$ = this.http.get<ItemOption[]>(`${this.environment.apiUrl}/membership/desciplineOptions`).pipe(
  //   shareReplay(1),
  //   catchError(this.handleError)
  // )
  // getDesciplineOptionsSignal = toSignal<ItemOption[], ItemOption[]>(this.getDesciplineOptions$, {initialValue: []});

  // levelOptions$ = this.http.get<ItemOption[]>(`${this.environment.apiUrl}/membership/levelOptions`).pipe(
  //   shareReplay(1),
  //   catchError(this.handleError)
  // )
  // levelOptionsSignal = toSignal<ItemOption[], ItemOption[]>(this.levelOptions$, {initialValue: []});

  private handleError(err: HttpErrorResponse): Observable<ItemOption[]> {
    return new Observable((observable)=> {
      let errorMessage = '';
      if (err.error instanceof ErrorEvent) {
        errorMessage = `An error occurred: ${err.error.message}`;
      } else {
        errorMessage = `Server returned code: ${err.status}, error message is: ${err.message}`;
      }
      console.error(errorMessage);
      observable.next([]);
      observable.complete();
    });
  }

  getActiveSeason(id: string) {
    const url = `${this.environment.apiUrl}/seasons/get/${id}`;
    return this.http.get(url);
  }

  getSeasonById(seasonId: string) {
    const url = `${this.environment.apiUrl}/seasons/get/${seasonId}`;
    return this.http.get(url);
  }

  getCurrentActiveSeason() {
    const url = `${this.environment.apiUrl}/seasons/getActiveSeason`;
    return this.http.get(url);
  }

  archieveMembershipIfNotMandatoryFieldFilled(membershipId: string, flag:boolean = false) {
    const url = `${this.environment.apiUrl}/membership/archieveMembershipIfNotMandatoryFieldFilled`;
    return this.http.post(url, {membershipId, flag});
  }

  getAdminNotes(payload: { assetType: string, assetId: string, page: number, seasonId: string, startDate?: string, endDate?: string, authors?: string[] }) {
    const url = `${this.environment.apiUrl}/admin-note/list`;
    return this.http.post<{ message: string, data: AdminNote[] }>(url, payload);
  }

  createAdminNote(payload: any) {
    const url = `${this.environment.apiUrl}/admin-note`;
    return this.http.post<{ message: string, data: AdminNote[] }>(url, payload);
  }

  getSecuredUrl(key: string): Observable<{ key: string, url: string }> {
    const url = `${this.environment.apiUrl}/file/url?key=${key}`;
    return this.http.get<{ key: string, url: string }>(url);
  }

  getAdminList(seasonId: string): Observable<{ data: { label: string, value: string }[]}> {
    const url = `${this.environment.apiUrl}/admin/list?seasonId=${seasonId}`;
    return this.http.get<{ data: { label: string, value: string }[]}>(url);
  }

  uploadFile(formData: FormData) {
    const url = `${this.environment.apiUrl}/file/upload`;
    return this.http.post(url, formData);
  }

  showStatus: Record<ShowStatusKey, string> = {
    [ShowStatus.AWAITING_PRIZE_LIST]: 'Awaiting Prize List Approval',
    [ShowStatus.PRE_ENTRY]: 'Pre Entry',
    [ShowStatus.PRIORITY_ENTRY]: 'Open to Priority Entry',
    [ShowStatus.OPEN_GENERAL_ENTRY]: 'Open to General Entry',
    [ShowStatus.CLOSED_ENTRY]: 'Closed to Entry',
    [ShowStatus.GENREATED_PROGRAM]: 'Program Generated',
    [ShowStatus.AWAITING_RESULT]: 'Awaiting Results',
    [ShowStatus.COMPLETED]: 'Complete'
  };

  getShowStatus(status: string): string {
    return this.showStatus[status as keyof typeof this.showStatus] || '';
  }

  getCellWidthClass(columnDef: string): string {
    const cellWidthMap: Record<string, string[]> = {
      small: ['stateCode', 'level', 'zone', 'showRegion', "classCode"],
      medium: ['IEA', 'city', 'zipCode', 'levels', 'zones', 'regions', 'school', 'grade', 'iea', "classDesc", "region", "points", "placing", "pointRider"],
      normal: ['memberType', 'mobile', 'disciplines', 'status', 'equisure', 'hippology', 'eligibilityDate', 'showId', 'showType', 'actions', 'feesPaidBy', 'teamStatus', 'region', 'phone', 'user_action', 'admin_action', 'riders', 'teamId', 'discipline', 'ineligibilityReason', "shortShowStartDate", "shortShowEndDate", 'showApplicationStatus', "assignRegion", 'riderName'],
      large: ['firstName', 'lastName', 'action', 'insuranceExpiration', 'showHost', 'prizeListStatus', 'teamName', 'role', 'primaryCaoch', 'showStartDate', 'showEndDate', 'showStatusDetail', 'timeStamp'],
      xlarge: ['email', 'streetAddress', 'teamNames', 'showName', 'showStatus', 'primaryCoachEmail', 'name', 'action_lg', 'classNameSplit' ],
      xxlarge: ['classes']
    };
  
    for (const [size, columns] of Object.entries(cellWidthMap)) {
      if (columns.includes(columnDef)) {
        return `${size}-cell`;
      }
    }
  
    return 'default-cell-width';
  }
}